import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { FloatingList } from './floating-list';
import {
  normalizeText,
  scrollEntryIntoView,
  useOutsidePress,
} from './floating-list.utils';

export interface DsMultiSelectOption<T extends string | number> {
  value: T;
  label: string;
  /** Shown right-aligned in mono, e.g. how many articles have this value. */
  count?: number;
}

interface DsMultiSelectProps<T extends string | number> {
  /** Name of the filter, e.g. "Hersteller"; the button reads "Hersteller: Alle" while nothing is chosen. */
  label: string;
  options: DsMultiSelectOption<T>[];
  selected: T[];
  onChange: (selected: T[]) => void;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** The list is at least this wide, even under a narrow button. */
  listMinWidth?: number;
}

/**
 * Button that opens a list with check boxes, e.g. for a filter. The list has a
 * search field on top (↑ ↓ move, Enter checks or unchecks, Esc closes) and a
 * row that clears the choice. Looks like {@link DsCombobox}.
 */
export function DsMultiSelect<T extends string | number>({
  label,
  options,
  selected,
  onChange,
  searchPlaceholder = 'Suchen…',
  disabled = false,
  className = '',
  listMinWidth = 240,
}: DsMultiSelectProps<T>) {
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);

  const search = normalizeText(query);
  const visible = useMemo(
    () =>
      search
        ? options.filter((o) => normalizeText(o.label).includes(search))
        : options,
    [options, search],
  );
  const active = highlight < visible.length ? highlight : -1;

  useEffect(() => {
    if (open && active >= 0) scrollEntryIntoView(listId, active);
  }, [open, active, listId]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlight(-1);
  }, []);

  const pressRefs = useMemo(() => [triggerRef, listRef], []);
  useOutsidePress(open, pressRefs, close);

  const toggle = (value: T) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlight(Math.min(active + 1, visible.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight(Math.max(active - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (active >= 0) toggle(visible[active].value);
        else if (visible.length === 1) toggle(visible[0].value);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
        break;
      case 'Tab':
        close();
        break;
    }
  };

  const summary =
    selected.length === 0
      ? `${label}: Alle`
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? `${label}: 1`)
        : `${label}: ${selected.length}`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={[
          'ds-multiselect-trigger',
          selected.length > 0 && 'has-selection',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        title={summary}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className="ds-multiselect-summary">{summary}</span>
        <span className="ds-combobox-chevron" aria-hidden>
          ▾
        </span>
      </button>
      <FloatingList
        ref={listRef}
        anchorRef={triggerRef}
        open={open}
        id={listId}
        role="listbox"
        ariaLabel={label}
        ariaMultiselectable
        minWidth={listMinWidth}
      >
        <div className="ds-floating-list-search">
          <input
            className="ds-input"
            value={query}
            placeholder={searchPlaceholder}
            aria-label={`${label} suchen`}
            aria-controls={listId}
            aria-activedescendant={
              active >= 0 ? `${listId}-opt-${active}` : undefined
            }
            autoFocus
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(e.target.value.trim() ? 0 : -1);
            }}
            onKeyDown={onSearchKeyDown}
          />
        </div>
        {selected.length > 0 && (
          <div
            className="ds-floating-list-option is-clear"
            onClick={() => onChange([])}
          >
            <span className="ds-floating-list-label">
              × Auswahl aufheben ({selected.length})
            </span>
          </div>
        )}
        {visible.map((o, index) => {
          const checked = selected.includes(o.value);
          return (
            <div
              key={o.value}
              id={`${listId}-opt-${index}`}
              role="option"
              aria-selected={checked}
              className={[
                'ds-floating-list-option',
                checked && 'is-checked',
                index === active && 'is-active',
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseEnter={() => setHighlight(index)}
              onClick={() => toggle(o.value)}
            >
              <span className="ds-floating-list-main">
                <span className="ds-floating-list-check" aria-hidden>
                  {checked ? '✓' : ''}
                </span>
                <span className="ds-floating-list-label">{o.label}</span>
              </span>
              {o.count !== undefined && (
                <span className="ds-floating-list-hint">{o.count}</span>
              )}
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="ds-floating-list-note">Keine Treffer</div>
        )}
      </FloatingList>
    </>
  );
}
